import { App } from "@capacitor/app";
import { memo, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatElapsed } from "../lib/audio/format-elapsed";
import {
  type CaptureAuditSummary,
  fetchAuditsForCaptures,
  mergeAuditPhoto,
} from "../lib/audits/fetch";
import { useAuth } from "../lib/auth/auth-context";
import { useIsAdmin } from "../lib/auth/use-is-admin";
import { fetchActiveBatch } from "../lib/captures/batches-fetch";
import {
  type CapturePhase,
  deriveCapturePhase,
  incompleteBadge,
  phaseBadge,
  secondsSinceNewestItem,
} from "../lib/captures/capture-phase";
import { secondsSinceLatest, secondsSinceReady } from "../lib/captures/extraction-status";
import { fetchCaptures } from "../lib/captures/fetch";
import {
  type CaptureItemSummary,
  fetchItemsForCapture,
  fetchItemsForCaptures,
} from "../lib/captures/items-fetch";
import {
  AUDIO_DELETED_NOTICE,
  captureAudioFateExplanation,
  captureDisplayTitle,
  captureHasOwnTitle,
  captureRetentionNotice,
  captureShowsAudioFate,
  formatCaptureDate,
} from "../lib/captures/labels";
import { reconcileCaptures } from "../lib/captures/reconcile";
import { fetchIngestRunStatus } from "../lib/captures/runs-fetch";
import type { Capture } from "../lib/captures/schema";
import { humanizeSpeakerLabelsInText } from "../lib/captures/speaker-label";
import {
  fetchSynthesisPresence,
  fetchSynthesisPresenceForCaptures,
} from "../lib/captures/synthesis-fetch";
import { countToCorrect } from "../lib/captures/to-correct";
import { useCurrentProject } from "../lib/projects/project-context";
import { projectDisplayName } from "../lib/projects/schema";
import { AudioPlayer } from "./ui/AudioPlayer";
import { Badge } from "./ui/Badge";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";
import { Icon } from "./ui/Icon";
import { useVisiblePoll } from "./use-visible-poll";

type ListState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; captures: Capture[] };

const IN_PROGRESS_STATUSES: ReadonlyArray<Capture["status"]> = [
  "uploading",
  "transcribing",
  "processing",
];

function hasInProgress(captures: Capture[]): boolean {
  return captures.some((c) => IN_PROGRESS_STATUSES.includes(c.status));
}

const POLL_INTERVAL_MS = 2000;
// Rafraîchissement régulier même quand rien n'est « en cours » : attrape la
// transition ready→transcription et garde la liste à jour tant qu'elle est ouverte.
const IDLE_REFRESH_MS = 10_000;

// Chargement progressif : on rend un premier lot puis un bouton « Voir plus »
// qui révèle un palier de plus. Le scoping projet et les filtres restent
// appliqués en amont — on ne tronque que le rendu final.
const PAGE_SIZE = 30;

// Date : déléguée à `formatCaptureDate` (labels.ts) — un import affiche la
// date seule (jour UTC), un enregistrement garde date + heure locales.

function typeLabel(type: Capture["type"]): string {
  return type === "rencontre" ? "Rencontre" : "Idée flash";
}

// Liseré de carte : on met en valeur l'EXCEPTION, pas la norme. « Prêt » (vert,
// majoritaire), les états neutres ET « en cours / en file » (info, transitoire)
// restent sans liseré → carte calme ; seuls les états qui demandent un regard
// portent un liseré : ambre (à reprendre), rouge (échec/erreur). Le ton vient
// de la source unique (phaseBadge).
function cardAccentClass(
  tone: "neutral" | "info" | "success" | "warning" | "danger",
): string | null {
  if (tone === "warning") return "from-amber-500 to-amber-400";
  if (tone === "danger") return "from-red-500 to-red-400";
  return null; // success / neutral / info : pas de liseré
}

interface CaptureRowProps {
  capture: Capture;
  projectName: string | null;
  items: CaptureItemSummary[];
  /** Les items de la liste ont-ils été chargés ? Tant que false, on ne conclut
   *  pas « à reprendre » sur une capture à 0 item (transitoire au chargement). */
  itemsLoaded?: boolean;
  /** Une synthèse existe-t-elle pour cette capture ? `undefined` tant que le lot
   *  n'a pas répondu — on ne rétrograde pas sur une ignorance passagère. C'est
   *  ce signal qui autorise le badge « Prêt » (arbitrage 2026-09-22). */
  hasSynthesis?: boolean;
  /** Audit le plus récent (admin-only, `null` si jamais audité ou non-admin).
   *  Chargé UNE FOIS au mount par `CapturesList` via un batch `.in()` paginé
   *  (review perf accueil 22-07) — jamais recréé par le poll : référence
   *  stable, compatible avec la mémoïsation de la rangée. Vue allégée : seuls
   *  les `flags` servent ici (badge « N à corriger »). */
  audit?: CaptureAuditSummary | null;
}

// T8 — la carte de l'accueil n'est plus un accordéon : elle est un résumé
// cliquable qui navigue vers la page dédiée (/captures/:id). Le lecteur audio
// reste le seul contrôle interactif séparé de la navigation ; tout le reste
// (dépliage, édition, transcript, audit détaillé, déplacer/supprimer) vit
// désormais sur la page dédiée.
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: orchestration UI d'une ligne de capture (statut/items/audio/badge qualité)
function CaptureRowBase({
  capture,
  projectName,
  items: initialItems,
  itemsLoaded = true,
  hasSynthesis: initialHasSynthesis,
  audit = null,
}: CaptureRowProps) {
  const navigate = useNavigate();
  const { client } = useAuth();
  // « Qualité d'extraction » = admin-only (Q2-B) : le badge « N à corriger »
  // ne s'affiche qu'aux admins (@flambeau.quebec).
  const isAdmin = useIsAdmin();
  // Local state pour items : reflète initialItems au mount, et se met à jour
  // via le poll d'extraction ci-dessous (keepPollingExtraction) sans refetch
  // global de la liste.
  const [items, setItems] = useState<CaptureItemSummary[]>(initialItems);
  useEffect(() => setItems(initialItems), [initialItems]);
  // Présence de synthèse : semée par le lot du parent, puis rafraîchie par le
  // poll d'extraction ci-dessous (la carte doit pouvoir passer à « Prêt » toute
  // seule quand le cron produit enfin la synthèse).
  const [hasSynthesis, setHasSynthesis] = useState<boolean | undefined>(initialHasSynthesis);
  useEffect(() => setHasSynthesis(initialHasSynthesis), [initialHasSynthesis]);

  // « À corriger » = items à problème de contenu (hallucination/factuel), pas
  // les retypages (Lot QA-2). Dépend de `audit` (prop, batchée par
  // CapturesList — review perf accueil 22-07 : le fetch PAR CARTE au mount
  // causait le N+1 ; `notAnchored` n'étant jamais posé par l'extraction et la
  // distinction « needsReview pour hallucination » ne vivant QUE dans
  // `audit.flags.hallucinated`, une hallucination flaggée par le juge perdait
  // son badge sans l'audit — catégorie primaire du badge, pas un cas
  // marginal : `audit` reste donc nécessaire ici, juste chargé en un seul
  // aller-retour réseau au lieu d'un par carte).
  const pendingCount = countToCorrect(items, audit);
  // Partie B3/B4 — état d'extraction (une capture transcrite peut n'avoir AUCUN
  // item parce que la chaîne async a été coupée, qu'un batch async est en cours,
  // ou que l'extraction a légitimement trouvé 0 besoin). On lit les ai_runs ingest
  // (échec/succès/tentatives) + l'existence d'un batch actif pour dériver l'état.
  const [ingestRunFailed, setIngestRunFailed] = useState(false);
  const [ingestSucceeded, setIngestSucceeded] = useState(false);
  const [ingestAttempts, setIngestAttempts] = useState(0);
  const [hasActiveBatch, setHasActiveBatch] = useState(false);
  // Derniers signes de vie de la file (SIG-261002-082836) : soumission du lot
  // actif et départ du dernier run d'extraction. Bornent l'attente « En file ».
  const [batchSubmittedAt, setBatchSubmittedAt] = useState<string | null>(null);
  const [ingestStartedAt, setIngestStartedAt] = useState<string | null>(null);

  // Détermine l'état de l'extraction d'items. On ne sonde ai_runs/llm_batches que
  // pour les captures à transcript présent mais 0 item (le cas suspect) — inutile
  // de tirer ces tables pour une capture saine (items présents) ou encore en cours.
  const transcriptPresent = capture.hasTranscript ?? Boolean(capture.transcriptRaw?.trim());
  const suspectNoItems =
    transcriptPresent &&
    items.length === 0 &&
    (capture.status === "ready" || capture.status === "processing" || capture.status === "error");
  useEffect(() => {
    if (!suspectNoItems) {
      setIngestRunFailed(false);
      setIngestSucceeded(false);
      setIngestAttempts(0);
      setHasActiveBatch(false);
      setBatchSubmittedAt(null);
      setIngestStartedAt(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      fetchIngestRunStatus(client, capture.id),
      fetchActiveBatch(client, capture.id),
    ]).then(([ingest, activeBatch]) => {
      if (cancelled) return;
      if (ingest.ok) {
        setIngestRunFailed(ingest.failed);
        setIngestSucceeded(ingest.succeeded);
        setIngestAttempts(ingest.attempts);
        setIngestStartedAt(ingest.latestRun?.startedAt ?? null);
      }
      setHasActiveBatch(Boolean(activeBatch));
      setBatchSubmittedAt(activeBatch?.submittedAt ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [client, capture.id, suspectNoItems]);

  // Phase visible UNIQUE (source de vérité partagée avec le flux
  // d'enregistrement). Combine le statut DB et l'état d'extraction dérivé.
  // `updatedAt` marque le passage `ready` (juste après transcription) : tant
  // qu'on est dans la fenêtre de grâce, on reste sur la phase amont
  // « transcription… » plutôt que d'émettre « en file » sans signal positif
  // (anti-flash).
  const phase: CapturePhase = deriveCapturePhase({
    captureStatus: capture.status,
    hasTranscript: transcriptPresent,
    itemCount: items.length,
    ingestRunFailed,
    hasActiveBatch,
    ingestSucceeded,
    ingestAttempts,
    secondsSinceReady: secondsSinceReady(capture.status, capture.updatedAt),
    secondsSinceQueueActivity: secondsSinceLatest([
      capture.updatedAt,
      batchSubmittedAt,
      ingestStartedAt,
    ]),
    itemsLoaded,
    hasSynthesis,
    secondsSinceNewestItem: secondsSinceNewestItem(items),
  });
  const badge = phaseBadge(phase);
  // Pastille SUPPLÉMENTAIRE (I2/C09, décision contrôleur) : indépendante du
  // STATUT DB — une capture `ready` peut très bien être incomplète (C02/C28).
  // Mais PAS en `preparing`/`problem` (correctif A7 #3a, C25) : pendant la
  // réparation d'une capture tronquée, `durationS` porte encore l'ancienne
  // valeur (avant que le serveur ne réécrive `elapsed_s`/`duration_s`) — la
  // comparer à `elapsedS` afficherait à tort « Enregistrement incomplet » sur
  // une prise qu'on est justement en train de compléter.
  const incomplete =
    phase === "preparing" || phase === "problem"
      ? null
      : incompleteBadge({
          durationS: capture.durationS,
          elapsedS: capture.elapsedS,
          missingAudioMs: capture.missingAudioMs,
        });
  // Re-poll tant que l'extraction est encore en train de se régler : « en file »
  // (batch async) OU capture fraîchement prête maintenue en « transcription… »
  // par l'anti-flash (on guette l'apparition du batch / des items). On re-vérifie
  // items + batch toutes les 15 s pour livrer automatiquement les items dès leur
  // arrivée (sans action ni refetch global). Dès que des items arrivent ou que
  // l'extraction se termine, la phase change et l'intervalle s'arrête.
  const keepPollingExtraction =
    phase === "queued" || (phase === "transcribing" && capture.status === "ready");
  useEffect(() => {
    if (!keepPollingExtraction) return;
    let cancelled = false;
    const tick = async () => {
      // La synthèse est relue à chaque tic : une carte maintenue en « En file »
      // par le verrou « Prêt » n'a pas d'autre moyen de se résoudre toute seule
      // (ses items, eux, sont déjà arrivés).
      const [it, batch, syn] = await Promise.all([
        fetchItemsForCapture(client, capture.id),
        fetchActiveBatch(client, capture.id),
        fetchSynthesisPresence(client, capture.id),
      ]);
      if (cancelled) return;
      if (it.ok && it.data.length > 0) setItems(it.data);
      setHasActiveBatch(Boolean(batch));
      setBatchSubmittedAt(batch?.submittedAt ?? null);
      if (syn.ok) setHasSynthesis(syn.data);
    };
    const id = setInterval(tick, 15_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [keepPollingExtraction, client, capture.id]);

  const retentionNotice = captureRetentionNotice(capture);
  // Contenu de la ligne de métadonnées, factorisé : le type et la date ne
  // s'affichent que si la capture porte un vrai titre (sinon ils doublonnent le
  // titre par défaut). « N items » n'existe que sur une capture analysée.
  const showTypeAndDate = captureHasOwnTitle(capture);
  const itemCount = capture.status === "ready" ? items.length : 0;
  const metaLine = { showTypeAndDate, itemCount, render: showTypeAndDate || itemCount > 0 };

  // Liseré : on met en valeur l'exception, pas la norme (la plupart des cartes
  // seront « Prêt »). success/neutre/info → pas de liseré ; ambre/rouge → liseré.
  // Le ton vient de la source unique (phaseBadge).
  const accentClass = cardAccentClass(badge.tone);

  return (
    <li className="list-none">
      <div className="relative flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-4 text-ink transition-colors hover:border-flambeau-200">
        {accentClass && (
          <span
            aria-hidden="true"
            className={`absolute inset-x-0 top-0 z-10 h-1 rounded-t-lg bg-gradient-to-r ${accentClass}`}
          />
        )}
        {/* Déclencheur de navigation — toute la zone titre/gist/méta/badges ouvre
            la page dédiée de la capture. Le lecteur audio, plus bas, est HORS de
            cette zone cliquable : il reste un contrôle séparé qui ne navigue pas. */}
        <button
          type="button"
          onClick={() => navigate(`/captures/${capture.id}`)}
          aria-label={`Ouvrir la capture : ${captureDisplayTitle(capture)}`}
          className="flex w-full cursor-pointer flex-col gap-2 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-orange"
        >
          <div className="flex items-baseline justify-between gap-1.5">
            <strong className="text-title-sm font-bold leading-tight tracking-[-0.01em] text-ink">
              {captureDisplayTitle(capture)}
            </strong>
            <span className="flex items-center gap-2 text-label text-muted tabular-nums">
              {formatElapsed(capture.durationS * 1000)}
              <Icon name="chevron-right" size={20} className="shrink-0 text-muted" />
            </span>
          </div>
          {/* Gist : le cœur de la discussion en une phrase, sous le titre.
              Prose du LLM → peut reprendre un `LOC_n` du transcript
              pseudonymisé ; l'accueil n'a pas d'annuaire de locuteurs (pas de
              capture ouverte), donc le repli « Personne N » suffit ici. */}
          {capture.gist && (
            <p data-gist className="m-0 text-body text-ink">
              {humanizeSpeakerLabelsInText(capture.gist)}
            </p>
          )}
          {/* Métadonnées : n'affiche que ce que le titre ne dit pas déjà. Sans
              titre saisi, le titre par défaut porte déjà type + date + heure ;
              on omet donc type et date pour ne montrer que le neuf (« N items »).
              Si rien de neuf à montrer, on ne rend pas de ligne vide. */}
          {metaLine.render && (
            <div data-meta className="text-small text-muted">
              {metaLine.showTypeAndDate && (
                <>
                  {typeLabel(capture.type)} · {formatCaptureDate(capture)}
                </>
              )}
              {metaLine.itemCount > 0 && (
                <span>
                  {metaLine.showTypeAndDate ? " · " : ""}
                  {metaLine.itemCount} item{metaLine.itemCount > 1 ? "s" : ""}
                </span>
              )}
            </div>
          )}
          {/* Échéance de rétention (Étape post-enregistrement) : pour une capture
              jamais analysée, l'audio est supprimé après le délai de rétention. */}
          {retentionNotice && <div className="text-small text-muted">{retentionNotice}</div>}
          <div className="flex flex-wrap items-center gap-1.5">
            {/* Badge d'état UNIQUE : libellé + ton issus de la source de vérité
                (phaseBadge), identique au flux d'enregistrement (CaptureScreen).
                Une phase = un libellé (fini la dérive accueil vs panneau et le
                flash transcription→en file). */}
            <Badge tone={badge.tone}>{badge.label}</Badge>
            {incomplete && <Badge tone={incomplete.tone}>{incomplete.label}</Badge>}
            {projectName && <Badge tone="neutral">{projectName}</Badge>}
            {/* Signal actionnable (Lot QA-2) : nombre d'items à problème de
                contenu. Admin-only. Le détail vit sur la page dédiée. */}
            {isAdmin && pendingCount > 0 && (
              <Badge tone="warning">
                <Icon name="alert-triangle" size={13} />
                {pendingCount} à corriger
              </Badge>
            )}
          </div>
        </button>

        {/* Lecteur audio — contrôle persistant, séparé de la navigation
            (composant DS AudioPlayer : play émeraude icône-seule, barre +
            égaliseur, <audio> caché, URL signée + lecture exclusive gérées en
            interne). Visible carte fermée comme aujourd'hui, hors du bouton de
            navigation. `captureShowsAudioFate` (labels.ts, correctif A7 #6) :
            faux pour une capture importée (jamais eu d'audio) ET pour une
            phase `preparing`/`problem` (audio_path nul par construction, ce
            n'est PAS une suppression — le bandeau de la fiche dit l'état). */}
        {captureShowsAudioFate(capture, phase) &&
          (capture.audioPath ? (
            <AudioPlayer
              audioPath={capture.audioPath}
              durationS={capture.durationS}
              fateExplanation={captureAudioFateExplanation(capture)}
            />
          ) : (
            // Audio supprimé (après rétention) : mention sobre sur une seule
            // ligne discrète (retour QA — plus de pastille en pointillés).
            <span className="text-small text-muted">{AUDIO_DELETED_NOTICE}</span>
          ))}
      </div>
    </li>
  );
}

// Perf accueil (22-07) : CaptureRow embarque ~30 useState/5 useEffect —
// coûteux à ré-exécuter pour rien à chaque tick de poll (10 s) qui ne change
// aucune donnée. `React.memo` fait le bail-out, mais SEULEMENT si TOUTES les
// props sont référentiellement stables d'un render à l'autre — sinon le memo
// ne sert à rien : `capture`/`items`/`projectName` sont donc stabilisés côté
// CapturesList (reconcileCaptures + useMemo, voir plus bas).
export const CaptureRow = memo(CaptureRowBase);

interface VisibleRow {
  capture: Capture;
  items: CaptureItemSummary[];
}

// Référence stable pour les captures sans items : un `?? []` inline créerait
// un tableau NEUF à chaque render, redéclenchant l'effet `[initialItems]` de
// chaque CaptureRow à chaque tick de poll.
const NO_ITEMS: CaptureItemSummary[] = [];

// Lignes visibles = captures du projet courant. Le scope projet est le seul
// filtre restant : tout le monde voit toutes les captures du projet.
function buildVisibleRows(
  captures: Capture[],
  itemsByCaptureId: Map<string, CaptureItemSummary[]>,
  projectId: string | null,
): VisibleRow[] {
  return captures.flatMap((c) => {
    if (projectId && c.projectId !== projectId) return [];
    return [{ capture: c, items: itemsByCaptureId.get(c.id) ?? NO_ITEMS }];
  });
}

// Applique un résultat de fetch au state « ready » en RÉCONCILIANT les
// captures avec celles déjà affichées (reconcileCaptures) : un refetch (poll,
// retour réseau/premier plan) renvoie toujours des objets NEUFS, même quand
// rien n'a changé — sans réconciliation, `capture` serait une référence
// instable à chaque tick et React.memo(CaptureRow) ne servirait à rien.
function toReadyState(prev: ListState, data: Capture[]): ListState {
  return {
    kind: "ready",
    captures: reconcileCaptures(prev.kind === "ready" ? prev.captures : [], data),
  };
}

// Liste des captures du projet courant, partagée par l'accueil (Home). Agrège
// captures + fetch items en batch (helper buildVisibleRows). Chaque ligne est
// un résumé cliquable qui navigue vers la page dédiée (/captures/:id) — la
// liste se charge par paliers (« Voir plus »).
export function CapturesList() {
  const { client } = useAuth();
  // Admin-only (Q2-B) : le batch d'audits ci-dessous n'a de sens que pour un
  // admin — RLS le refuserait de toute façon pour un non-admin.
  const isAdmin = useIsAdmin();
  const { currentProjectId, projects } = useCurrentProject();
  const [state, setState] = useState<ListState>({ kind: "loading" });
  // Compteur de relance : incrémenté par « Réessayer », il figure dans les
  // dépendances de l'effet de chargement — pas besoin d'extraire l'effet.
  const [reloadKey, setReloadKey] = useState(0);
  const [itemsByCaptureId, setItemsByCaptureId] = useState<Map<string, CaptureItemSummary[]>>(
    () => new Map(),
  );
  // Audits (badge « N à corriger », admin-only) : UNE SEULE lecture `.in()`
  // paginée au mount pour toutes les captures ready (review perf accueil 22-07
  // — remplace un fetch PAR CARTE qui causait ~30 requêtes à l'ouverture).
  // Jamais retouchée par le poll : référence stable, pas de recréation à
  // chaque tick (indispensable à la mémoïsation de CaptureRow).
  const [auditByCaptureId, setAuditByCaptureId] = useState<Map<string, CaptureAuditSummary>>(
    () => new Map(),
  );
  // Les items arrivent APRÈS les captures (fetch séparé). Tant que false, on ne
  // conclut pas « à reprendre » sur une capture à 0 item → évite un flash d'état.
  const [itemsLoaded, setItemsLoaded] = useState(false);
  // Captures dont la chaîne est allée jusqu'au bout (une synthèse existe) :
  // UNE SEULE requête `.in()` au mount, même patron que les items et les audits.
  // `null` = pas encore chargé → chaque carte reçoit `undefined` et ne rétrograde
  // rien tant qu'on ne sait pas.
  const [synthesisByCaptureId, setSynthesisByCaptureId] = useState<Set<string> | null>(null);
  // Chargement progressif : nombre de captures rendues. Réinitialisé quand les
  // filtres changent pour repartir du premier palier.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // `reloadKey` n'est pas une valeur LUE par l'effet : c'est son déclencheur.
  // Le bouton « Réessayer » l'incrémente pour relancer la lecture, ce qui
  // évite d'extraire le corps de l'effet (et sa garde `cancelled`) dans un
  // useCallback juste pour pouvoir le rappeler.
  // biome-ignore lint/correctness/useExhaustiveDependencies: déclencheur de relance, pas une dépendance lue
  useEffect(() => {
    let cancelled = false;

    // Fetch items (toujours) + audits (admin-only) en batch pour les captures
    // ready — un seul `.in()` chacun, une seule fois au mount (évite le N+1).
    function loadReadyDetails(ids: string[]) {
      fetchItemsForCaptures(client, ids).then((r) => {
        if (cancelled) return;
        if (r.ok) {
          setItemsByCaptureId(r.data);
          // `complete` = la photo est ENTIÈRE. Une réponse partielle (plafond
          // PostgREST, pagination bornée) laisse `itemsLoaded` à false : les
          // captures sans item vu restent « prêt » au lieu d'affirmer
          // « Aucun item détecté » sur une information qu'on n'a pas.
          // Bogue P1 du 2026-09-22 — voir items-fetch.ts.
          setItemsLoaded(r.complete);
        }
      });
      fetchSynthesisPresenceForCaptures(client, ids).then((r) => {
        if (cancelled) return;
        if (r.ok) setSynthesisByCaptureId(r.data);
      });
      if (isAdmin) {
        fetchAuditsForCaptures(client, ids).then((r) => {
          if (cancelled) return;
          if (!r.ok) return;
          // Photo PARTIELLE (plafond PostgREST, pagination bornée) : une capture
          // absente de la réponse n'est pas une capture sans audit. On ne
          // remplace ce que l'on affiche que sur une photo ENTIÈRE ; sinon on
          // fusionne, pour qu'une troncature ne puisse jamais faire disparaître
          // un badge « N à corriger » déjà connu. Cf. audits/fetch.ts.
          setAuditByCaptureId((prev) => mergeAuditPhoto(prev, r.data, r.complete));
        });
      }
    }

    // La liste des projets vient du ProjectContext (source unique) ; ici on ne
    // charge que les captures.
    fetchCaptures(client).then((cr) => {
      if (cancelled) return;
      if (!cr.ok) {
        setState({ kind: "error", message: cr.error });
        return;
      }
      setState((prev) => toReadyState(prev, cr.data));
      const ids = cr.data.filter((c) => c.status === "ready").map((c) => c.id);
      if (ids.length > 0) {
        loadReadyDetails(ids);
      } else {
        setItemsLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [client, isAdmin, reloadKey]);

  // Réseau revenu / retour au 1er plan : recharger la liste — une capture restée
  // en attente vient peut-être d'être re-synchronisée par le filet global, on la
  // fait apparaître sans action manuelle.
  useEffect(() => {
    const refetch = () => {
      void fetchCaptures(client).then((r) => {
        if (r.ok) setState((prev) => toReadyState(prev, r.data));
      });
    };
    window.addEventListener("online", refetch);
    const handle = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) refetch();
    });
    return () => {
      window.removeEventListener("online", refetch);
      void handle.then((h) => h.remove());
    };
  }, [client]);

  // Auto-refresh tant qu'au moins une capture est en cours (uploading,
  // transcribing, processing). S'arrête seul quand tout est en état terminal.
  // Poll rapide tant qu'une capture est en cours, plus lent sinon — mais on
  // rafraîchit TOUJOURS tant que l'écran est ouvert ET visible, pour attraper
  // la transition ready→transcription. Onglet masqué : useVisiblePoll suspend
  // les ticks (zéro requête) et refetch immédiatement au retour visible.
  const pollIntervalMs =
    state.kind === "ready"
      ? hasInProgress(state.captures)
        ? POLL_INTERVAL_MS
        : IDLE_REFRESH_MS
      : null;
  useVisiblePoll(() => {
    void fetchCaptures(client).then((r) => {
      if (r.ok) setState((prev) => toReadyState(prev, r.data));
    });
  }, pollIntervalMs);

  // Stabilisation (perf accueil, 22-07) : recalculés seulement quand leurs
  // dépendances changent réellement — évite de reconstruire la Map/le tableau
  // (et de redonner une identité neuve à des valeurs déjà stables) à chaque
  // render de CapturesList (ex. toggle de `itemsLoaded`, clic « Voir plus »).
  const projectNameById = useMemo(
    () => new Map(projects.map((p) => [p.id, projectDisplayName(p)])),
    [projects],
  );
  const allRows = useMemo(
    () =>
      state.kind === "ready"
        ? buildVisibleRows(state.captures, itemsByCaptureId, currentProjectId)
        : [],
    [state, itemsByCaptureId, currentProjectId],
  );
  const visibleRows = allRows.slice(0, visibleCount);
  const hasMore = allRows.length > visibleRows.length;

  // Réinitialise le palier quand le projet change (sinon on garderait un gros
  // palier hérité d'un projet plus large). Reset volontairement déclenché par
  // currentProjectId, même s'il n'est pas lu dans le corps de l'effet.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset volontaire sur changement de projet
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [currentProjectId]);

  if (state.kind === "loading") {
    return <p className="m-0 py-10 text-center text-small text-muted">Chargement…</p>;
  }

  if (state.kind === "error") {
    // DS §6.13 : `state.message` est déjà une phrase rédigée par
    // `lib/captures/fetch` (cause + geste) — plus de préfixe « Erreur : », qui
    // collait un mot d'app devant « TypeError: Load failed ». Le bouton que la
    // phrase nomme est ici : sans lui, la troisième exigence du §6.13 (le
    // geste) serait une promesse en l'air.
    return (
      <Card variant="danger">
        <p className="m-0 text-small">{state.message}</p>
        <Button
          type="button"
          variant="secondary"
          size="md"
          className="mt-3 self-start"
          onClick={() => {
            setState({ kind: "loading" });
            setReloadKey((k) => k + 1);
          }}
        >
          <Icon name="rotate-cw" size={16} />
          Réessayer
        </Button>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {visibleRows.length === 0 && (
        <div className="rounded-xl border border-dashed border-line bg-ground px-5 py-12 text-center">
          <p className="m-0 text-small text-muted">
            Aucune capture dans ce projet pour l'instant. Lance un enregistrement ou importe une
            transcription pour commencer.
          </p>
        </div>
      )}

      {visibleRows.length > 0 && (
        <ul className="m-0 flex flex-col gap-3 p-0">
          {visibleRows.map(({ capture: c, items }) => (
            <CaptureRow
              key={c.id}
              capture={c}
              projectName={projectNameById.get(c.projectId) ?? null}
              items={items}
              itemsLoaded={itemsLoaded}
              hasSynthesis={synthesisByCaptureId ? synthesisByCaptureId.has(c.id) : undefined}
              audit={auditByCaptureId.get(c.id) ?? null}
            />
          ))}
        </ul>
      )}

      {hasMore && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
          className="self-center"
        >
          Voir plus ({allRows.length - visibleRows.length} restante
          {allRows.length - visibleRows.length > 1 ? "s" : ""})
        </Button>
      )}
    </div>
  );
}
